import React from 'react';
import { cn } from '../../lib/utils';

/**
 * El marco de una captura del producto, en las filas de la sección de producto.
 *
 *
 * POR QUÉ YA NO SE SALE DEL BORDE POR DEFECTO
 *
 * La primera versión anclaba la captura a un lado y la desbordaba hacia afuera
 * con un margen negativo, para que quedara cortada contra el borde de la
 * pantalla. La idea era buena y sigue estando disponible en `desborde`, pero
 * suelta no funciona, y el motivo es geométrico:
 *
 * La fila vive en un contenedor centrado. En una ventana de 1920 ese
 * contenedor mide 1152, o sea que quedan 384px de aire a cada lado. Un tirón
 * del 22% de la columna son ~170px: no llega ni de cerca al borde. Entonces la
 * imagen no se corta contra nada — simplemente arranca antes, y como el texto
 * se queda donde estaba, TODA LA FILA SE VE CORRIDA A LA IZQUIERDA, con un
 * hueco grande a la derecha. El efecto sólo aparecía en ventanas angostas,
 * donde el contenedor sí toca los bordes.
 *
 * O sea que el mismo valor produce dos cosas distintas según el ancho: recorte
 * en pantallas chicas, desbalance en pantallas grandes. Por eso el defecto es
 * cero y la fila queda centrada de verdad.
 *
 * Si se quiere recuperar el corte contra el borde, no alcanza con subir
 * `desborde`: hay que sacar el ancho máximo del contenedor de la sección para
 * ese lado, porque es el contenedor —y no la imagen— el que decide dónde está
 * el borde.
 *
 * `loading="lazy"` y `decoding="async"` porque ninguna de estas capturas está
 * arriba del pliegue, y pesan ~250 kB cada una.
 *
 * @param src        La captura.
 * @param alt        Qué se ve. No decorativa: es la prueba del argumento.
 * @param haciaLa    'derecha' | 'izquierda'. De qué lado desborda, si desborda.
 * @param desborde   Cuánto se sale, en porcentaje de la columna. 0 = centrada.
 * @param className  Para el alto o el margen; el componente no decide eso.
 */
export default function CapturaDeProducto({
  src,
  alt,
  haciaLa = 'derecha',
  desborde = 0,
  className,
}) {
  /* Margen negativo y no `translate`: los transforms no reservan espacio, así
     que la fila seguiría midiendo el ancho entero y quedaría un hueco del otro
     lado. El ancho compensa exactamente lo que se corrió, para que el borde de
     adentro nunca llegue a pisar la columna del texto. */
  const estilo = desborde
    ? {
        [haciaLa === 'derecha' ? 'marginRight' : 'marginLeft']: `-${desborde}%`,
        width: `${100 + desborde}%`,
        maxWidth: 'none',
      }
    : undefined;

  return (
    <div className={cn('relative', className)} style={estilo}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="w-full rounded-2xl border border-neutral-200/80 bg-white shadow-[0_24px_70px_-30px_rgba(15,15,20,0.45)]"
      />
    </div>
  );
}
